import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DSSetup, MetricAnomalies, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type { Props as MetricSetupProps } from 'webapp/models-custom/metric-setup';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type {
	OnChangeProp as MetricSetupOnChangeProp,
	OnChangePropValue as MetricSetupOnChangePropValue,
} from 'webapp/models-custom/metric-setup-base';
import type MetricAnomalyRecord from 'webapp/models/metric-anomaly';

export default class MetricAnomaly extends BufferedModel<
	MetricAnomalyRecord,
	RecordData.MetricAnomaly.Serialized
> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	// ------- props from / to ED record ------

	get id() {
		return this.model.id;
	}

	get metricSetupKey(): string | null {
		return this.getBuffered('metricSetupKey');
	}

	set metricSetupKey(value: string | null) {
		this.setBuffered('metricSetupKey', value);
	}

	get metricSetupSpaceAccessId(): number | null {
		return this.getBuffered('metricSetupSpaceAccessId');
	}

	set metricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('metricSetupSpaceAccessId', value);
	}

	get metricSetupDimensions(): string[] {
		return this.getBuffered('metricSetupDimensions');
	}

	set metricSetupDimensions(value: string[]) {
		this.setBuffered('metricSetupDimensions', value);
	}

	get metricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('metricSetupFilters');
	}

	set metricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('metricSetupFilters', value);
	}

	get settings(): MetricAnomalies.Settings {
		return this.getBuffered('settings');
	}

	set settings(value: MetricAnomalies.Settings) {
		this.setBuffered('settings', value);
	}

	private handleMetricSetupUpdate(
		prop: MetricSetupOnChangeProp,
		value: MetricSetupOnChangePropValue,
	) {
		switch (prop) {
			case 'key':
				this.metricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.metricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.metricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.metricSetupFilters = value as DSSetup.MetricSetupFiltersData;
				break;
		}
	}

	@cached
	get metricSetup(): MetricSetup {
		const props: MetricSetupProps = {
			key: this.metricSetupKey,
			spaceAccessId: this.metricSetupSpaceAccessId,
			dimensions: this.metricSetupDimensions,
			filters: this.metricSetupFilters,
			allowAllDimensionValues: false,
			allowAllUnits: false,
		};

		const metricSetup = new MetricSetup(props, this.handleMetricSetupUpdate.bind(this));

		return metricSetup;
	}

	get mappedRecipients() {
		return this.mmUsersService.getMappedRecipients([], [], this.settings.notifications.slack);
	}

	createAnotherModel() {
		return new MetricAnomaly({ model: this.model });
	}

	static override modelName = ModelName.METRIC_ANOMALY; // store record type
}
